import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Formik, Form, FormikState, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Box, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import BranchsDropdown from '@components/BranchsDropdown';
import If from '@components/If';
import ViewerTextField from '@components/ViewerTextField';
import RegexPatterns from '@core/constants/RegexPatterns';
import useConfirm from '@core/hooks/useConfirm';
import useOrganizationInfo from '@core/hooks/useOrganizationInfo';
import { RoleName } from '@core/types';
import formatToSpanishDate from '@core/utils/formatDateToSpanishDate';
import updateStudy from 'app/features/study/api/updateStudy';
import useShareByEmailOrPhone from 'app/features/study/hooks/useShareByEmailOrPhone';
import Actions from '../Actions';
import { StudyInfoProps } from './StudyInfoProps';
import useStudyInfoState from './useStudyInfoState';

const validationSchema = Yup.object().shape({
  patientsAge: Yup.string(),
  patientsGivenName: Yup.string(),
  patientsSurname: Yup.string(),
  patientsGender: Yup.string().oneOf(['M', 'F', 'O'], 'El género debe ser M, F u O').max(1),
  patientsEmail: Yup.string(),
  patientsPhoneNumber: Yup.string(),
  studyDescription: Yup.string(),
  referringPhysiciansName: Yup.string(),
});

const StudyInfo = ({ study, role, refetch, update, isUpdating, onClose }: StudyInfoProps) => {
  const {
    studyDate,
    patientsFirstName,
    patientsLastName,
    patientsBirthDate,
    patientsAge,
    patientsGender,
    patientsEmail,
    patientsPhoneNumber,
    studyDescription,
    referringPhysiciansName,
    studyInstanceUid,
    shareUrl,
  } = study;

  const { showSnackbar, sx } = useStudyInfoState();
  const { share, shareWhatsapp, isSharing } = useShareByEmailOrPhone(study.studyInstanceUid);
  const { organizationInfo } = useOrganizationInfo();
  const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>(study.branchId);

  const initialValues = useMemo(() => {
    const phoneNumber = patientsPhoneNumber;

    const matches = phoneNumber?.match(RegexPatterns.PHONE_NUMBER) || null;
    return {
      patientsBirthDate: dayjs(patientsBirthDate) || null,
      patientsGivenName: patientsFirstName || '',
      patientsSurname: patientsLastName || '',
      patientsAge: patientsAge || '',
      patientsGender: patientsGender?.trim() || 'O',
      patientsEmail: patientsEmail || '',
      patientsPhoneNumber: matches?.[2] || '',
      studyDescription: studyDescription || '',
      referringPhysiciansName: referringPhysiciansName || '',
      patientsCountryCodePhoneNumber: matches?.[1] || '',
      patientId: study.patientId || '',
      branchId: study.branchId,
    };
  }, [
    patientsBirthDate,
    patientsFirstName,
    patientsLastName,
    patientsAge,
    patientsGender,
    patientsEmail,
    patientsPhoneNumber,
    studyDescription,
    referringPhysiciansName,
    study.patientId,
    study.branchId,
  ]);

  const { isConfirmed } = useConfirm();

  const handleSharingByWhatsAppAndEmail = async (
    phoneNumber: string,
    email: string,
    name: string,
  ) => {
    const confirmed = await isConfirmed({
      title: '¿Deseas compartir el estudio al email y número registrado?',
      message: `Has actualizado el número de teléfono a +${phoneNumber} y email a ${email}. ¿Te gustaría compartir el estudio a este número y correo electrónico?`,
      type: 'info',
    });
    if (confirmed) {
      await share({ emailOrPhone: email, name, language: 'es_MX' });
      await shareWhatsapp({ emailOrPhone: phoneNumber, language: '', name });
    }

    return confirmed;
  };

  const handleShareByEmail = async (email: string, name: string) => {
    const confirmed = await isConfirmed({
      title: '¿Deseas compartir el estudio por correo electrónico?',
      message: `Has actualizado el correo electrónico a ${email}. ¿Te gustaría compartir el estudio por correo electrónico?`,
      type: 'info',
    });
    if (confirmed) {
      await share({ emailOrPhone: email, name, language: 'es_MX' });
    }
    return confirmed;
  };

  const handleShareByPhone = async (phoneNumber: string, name: string) => {
    const confirmed = await isConfirmed({
      title: '¿Deseas compartir el estudio al número registrado?',
      message: `Has actualizado el número de teléfono a +${phoneNumber}. ¿Te gustaría compartir el estudio a este número?`,
      type: 'info',
    });
    if (confirmed) {
      await shareWhatsapp({
        emailOrPhone: phoneNumber,
        language: 'es_MX',
        name,
        organizationPhone: organizationInfo.organizationPhone,
        organizationName: organizationInfo.organizationName,
        shareLink: shareUrl,
      });
    }
    return confirmed;
  };

  const handleShareChangedEmailAndPhoneNumber = async (
    phoneNumber: string,
    email: string,
    name: string,
  ) => {
    const hasChangedPhoneNumber =
      phoneNumber !== patientsPhoneNumber && RegexPatterns.PHONE_NUMBER.test(phoneNumber);
    const hasChangedEmail = email !== initialValues.patientsEmail && email !== '';
    const formattedPhoneNumber = phoneNumber.replace(/\((\d+)\)(\d+)/, '$1$2');

    if (hasChangedPhoneNumber && hasChangedEmail) {
      return await handleSharingByWhatsAppAndEmail(formattedPhoneNumber, email, name);
    } else if (hasChangedEmail) {
      return await handleShareByEmail(email, name);
    } else if (hasChangedPhoneNumber) {
      return await handleShareByPhone(formattedPhoneNumber, name);
    }
  };

  const onSubmit = async (propValues: typeof initialValues) => {
    const values = { ...propValues };
    values.patientsPhoneNumber = `(${values.patientsCountryCodePhoneNumber})${values.patientsPhoneNumber}`;
    const patientsBirthDateFormatted =
      values.patientsBirthDate && values.patientsBirthDate.isValid()
        ? values.patientsBirthDate.toISOString()
        : null;
    const fullName = values.patientsGivenName + ' ' + values.patientsSurname;

    const confirmed = await handleShareChangedEmailAndPhoneNumber(
      values.patientsPhoneNumber,
      values.patientsEmail,
      fullName,
    );

    let successMessage = 'La información del estudio ha sido guardada';

    if (confirmed) {
      successMessage =
        'La información del estudio ha sido guardada exitosamente. Además, se ha compartido a los medios confirmados.';
    }

    const payload = { ...values, studyInstanceUid };

    // Evodicom Service
    const response = await update({
      ...payload,
      patientsBirthDate: patientsBirthDateFormatted,
      branchId: selectedBranchId,
    });

    if (response.success) {
      // Dicom Service
      await updateStudy(studyInstanceUid, { ...payload });
      showSnackbar({
        message: successMessage,
        title: 'Guardado con éxito',
        type: 'success',
      });
      refetch();
    } else {
      showSnackbar({
        message: 'La información no ha sido guardada',
        title: 'Ocurrió un error',
        type: 'error',
      });
    }
  };

  const handleCancel = (
    resetForm: (
      nextState?: Partial<FormikState<typeof initialValues> | undefined> | undefined,
    ) => void,
  ) => {
    resetForm();
    if (onClose) onClose();
  };

  const formikRef = React.useRef<FormikProps<typeof initialValues>>(null);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, handleChange, handleBlur, errors, touched, handleSubmit, resetForm }) => (
        <Form>
          <Stack sx={sx.root}>
            <ViewerTextField
              className="viewer-textfield"
              label="Fecha"
              value={formatToSpanishDate(studyDate!, { full: true })}
              readOnly
            />

            <ViewerTextField
              className="viewer-textfield"
              label="Descripción del Estudio"
              name="studyDescription"
              value={values.studyDescription}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.studyDescription && Boolean(errors.studyDescription)}
              helperText={touched.studyDescription && errors.studyDescription}
              readOnly={role === RoleName.Referrer || role === RoleName.Patient}
            />
            <ViewerTextField
              className="viewer-textfield"
              label="Doctor:"
              name="referringPhysiciansName"
              value={values.referringPhysiciansName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.referringPhysiciansName && Boolean(errors.referringPhysiciansName)}
              helperText={touched.referringPhysiciansName && errors.referringPhysiciansName}
              readOnly={role === RoleName.Referrer || role === RoleName.Patient}
            />
            <Box sx={sx.branchContainer}>
              <Typography sx={sx.branchLabel}>Sucursal:</Typography>
              <BranchsDropdown
                handleSelectedBranchChange={(branch) => setSelectedBranchId(branch?.id)}
                initialValue={study.branchId}
                hidePlaceholder
                sx={sx.branchDropdown}
              />
            </Box>
            <If
              condition={
                role === RoleName.Radiologist ||
                role === RoleName.Technician ||
                role === RoleName.System
              }
            >
              <Actions
                onAccept={handleSubmit}
                onCancel={() => handleCancel(resetForm)}
                isLoading={isUpdating || isSharing}
              />
            </If>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default StudyInfo;
